// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import DatePicker from "@insite/mobius/DatePicker";
import DynamicDropdown from "@insite/mobius/DynamicDropdown";
import { FormFieldStyle } from "@insite/mobius/FormField";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Radio from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import Select from "@insite/mobius/Select/Select";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { SectionContainer, SectionTitle } from "@tps/App/Widgets/DesignSystem/DesignSystem.helpers";
import { dynamicDropdownOptions, longText } from "@tps/App/Widgets/DesignSystem/DummyData/DummyData";
import Search from "@tps/XDL/Icons/Search";
import { xValidateOnSubmit } from "@tps/XDL/Utilities";
import React, { FC, useState } from "react";
import styled, { css } from "styled-components";

interface FormTabStyles {
    gridItem?: GridItemProps;
    smallGridItem?: GridItemProps;
    formWrapper?: InjectableCss;
    columnFormWrapper?: InjectableCss;
}

const formTabStyles: FormTabStyles = {
    gridItem: {
        width: 12,
        css: css`
            flex-wrap: wrap;
            padding-bottom: 40px;
        `,
    },
    smallGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-wrap: wrap;
            padding-bottom: 40px;
        `,
    },
    formWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            margin-top: 20px;
            flex-wrap: wrap;
            ${FormFieldStyle} {
                max-width: 250px;
            }
        `,
    },
    columnFormWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            margin-top: 20px;
            ${FormFieldStyle} {
                max-width: 250px;
            }
        `,
    },
};

const styles = formTabStyles;
const FormTab: FC = () => {
    const [radioValue, setRadioValue] = useState("All");
    const onRadioChangeHandler = (e: any) => {
        setRadioValue(e.target.value);
    };

    return (
        <SectionContainer>
            <GridItem {...styles.gridItem}>
                <SectionTitle>TextField</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <TextField
                        label="User Name - Required"
                        required
                        data-test-selector="userName"
                        placeholder="Placeholder"
                    />
                    <TextField
                        label="User Name - Disabled"
                        disabled
                        data-test-selector="userName"
                        placeholder="Placeholder"
                    />
                    <TextField label="User Name - Error" error="Error" data-test-selector="userName" />
                    <TextField
                        label="With Icon"
                        data-test-selector="withIcon"
                        iconProps={{
                            src: Search,
                        }}
                        iconClickableProps={{
                            type: "button",
                        }}
                    />
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <SectionTitle>TextArea</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <TextArea placeholder="Type some content" required label="Description - Required" />
                    <TextArea placeholder="Type some content" defaultValue={longText} label="Description - Long Text" />
                    <TextArea
                        placeholder="Type some content"
                        defaultValue={longText}
                        disabled
                        label="Description - Disabled"
                    />
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <SectionTitle>Select</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <Select label="Select the best one" required placeholder="Select" onChange={event => {}}>
                        <option value="Day">{translate("Day")}</option>
                        <option value="Month">{translate("Month")}</option>
                    </Select>
                    <Select label="Select the best one" disabled placeholder="Select" onChange={event => {}}>
                        <option value="Day">{translate("Day")}</option>
                        <option value="Month">{translate("Month")}</option>
                    </Select>
                    <Select label="Pagination" required placeholder="Pagination" onChange={event => {}}>
                        <option value={8}>8</option>
                        <option value={16}>16</option>
                        <option value={32}>32</option>
                    </Select>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <SectionTitle>DatePicker</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <DatePicker label="Date" />
                    <DatePicker label="Date (required)" required />
                    <DatePicker label="Date (selected)" selectedDay={new Date(2019, 8, 12)} />
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Checkbox</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <CheckboxGroup label="Checkbox Group Label">
                        <Checkbox checked>Checked</Checkbox>
                        <Checkbox>Unchecked</Checkbox>
                        <Checkbox checked disabled>
                            Disabled Checked
                        </Checkbox>
                        <Checkbox checked="indeterminate" disabled>
                            Disabled Indeterminate
                        </Checkbox>
                        <Checkbox disabled>Disabled</Checkbox>
                    </CheckboxGroup>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Checkbox</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <CheckboxGroup label="Checkbox Group Label">
                        <Checkbox variant="toggle" checked>
                            Checked
                        </Checkbox>
                        <Checkbox variant="toggle">Unchecked</Checkbox>
                        <Checkbox variant="toggle" checked disabled>
                            Disabled Checked
                        </Checkbox>
                    </CheckboxGroup>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Radio</SectionTitle>
                <StyledWrapper {...styles.columnFormWrapper}>
                    <RadioGroup value={radioValue} onChangeHandler={onRadioChangeHandler}>
                        <Radio key="All">All</Radio>
                        <Radio key="Unread">Unread</Radio>
                        <Radio key="Read">Read</Radio>
                        <Radio key="Disabled" disabled={true}>
                            Disabled
                        </Radio>
                    </RadioGroup>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <SectionTitle>Dynamic Dropdown</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <DynamicDropdown
                        options={dynamicDropdownOptions}
                        label="Dynamic Dropdown"
                        onSelectionChange={(value?: string) => {}}
                    />
                </StyledWrapper>
            </GridItem>

            <GridItem {...styles.gridItem}>
                <SectionTitle>Form Validation</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <FormExample />
                </StyledWrapper>
            </GridItem>
        </SectionContainer>
    );
};

export default FormTab;

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const FormExample = () => {
    const formId = "myFormId";
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");

    const handleEmailChange = (e: any) => {
        setEmail(e.target.value);
    };

    const handleMessageChange = (e: any) => {
        setMessage(e.target.value);
    };

    const handleSubmit = (e: any) => {
        e.preventDefault();
        const errors = xValidateOnSubmit(formId);
        // eslint-disable-next-line no-console
        console.log({ errors });
        if (errors.length > 0) {
            return;
        }
        // eslint-disable-next-line no-console
        console.log("Submitted");
    };

    return (
        <StyledForm onSubmit={handleSubmit}>
            <TextField
                label={translate("Email")}
                required={true}
                onChange={handleEmailChange}
                value={email}
                type="email"
                {...{ formId }}
            />

            <TextArea
                label={translate("Your Message")}
                onChange={handleMessageChange}
                required={true}
                value={message}
                {...{ formId }}
            />
            <Button type="submit">{translate("send")}</Button>
        </StyledForm>
    );
};
